import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTemplates, fetchTemplate, resizeTemplate, saveTemplate, updateTemplateMeta } from '../api/client.js';
import { ResizeTemplateDialog } from './ResizeTemplateDialog.js';
import type { TemplateSummary } from '../types/index.js';
import { UploadTemplateDialog } from './UploadTemplateDialog.js';

function triggerDownload(filename: string, json: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface UploadPending {
  template: TemplateSummary;
  parsedJson: Record<string, unknown>;
  fileName: string;
}

export function TemplateList() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [uploadTarget, setUploadTarget] = useState<TemplateSummary | null>(null);
  const [uploadPending, setUploadPending] = useState<UploadPending | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [resizing, setResizing] = useState<TemplateSummary | null>(null);

  async function handleBleed(t: TemplateSummary): Promise<void> {
    const raw = window.prompt(`Print bleed per side in mm for "${t.label}" (coloured rectangles at the edge are stretched into it, images only where you drag them; empty = server default):`, t.bleedMm == null ? '' : String(t.bleedMm));
    if (raw === null) return;
    const bleedMm = raw.trim() === '' ? null : Number(raw.replace(',', '.'));
    if (bleedMm !== null && !(bleedMm >= 0 && bleedMm <= 20)) return setError('Bleed must be between 0 and 20 mm');
    try {
      await updateTemplateMeta(t.id, { bleedMm });
      setTemplates(await fetchTemplates());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleResize(r: { widthMm: number; heightMm: number; mode: 'keep' | 'scale'; createdBy: string }, target: TemplateSummary | null = resizing): Promise<void> {
    if (!target) return;
    try {
      const res = await resizeTemplate(target.id, r);
      setResizing(null);
      setTemplates(await fetchTemplates());
      if (res.outside.length) window.alert(`Resized. These elements now lie (partly) outside the label: ${res.outside.join(', ')}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleRename(t: TemplateSummary): Promise<void> {
    const label = window.prompt(`New name for "${t.label}" (the ID ${t.id} stays, so Filling Master keeps its assignments):`, t.label);
    if (label === null || !label.trim() || label.trim() === t.label) return;
    try {
      await updateTemplateMeta(t.id, { label: label.trim() });
      setTemplates(await fetchTemplates());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleArchive(t: TemplateSummary, archived: boolean): Promise<void> {
    if (archived && !window.confirm(`Archive "${t.label}"? It is hidden in Filling Master; packs that still use it keep printing with it.`)) return;
    try {
      await updateTemplateMeta(t.id, { archived });
      setTemplates(await fetchTemplates());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    fetchTemplates()
      .then(setTemplates)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, []);

  async function handleDownload(t: TemplateSummary): Promise<void> {
    try {
      setDownloading(t.id);
      const detail = await fetchTemplate(t.id);
      triggerDownload(`${t.id}.json`, JSON.stringify(detail.template, null, 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(null);
    }
  }

  function handleUploadClick(t: TemplateSummary): void {
    setUploadTarget(t);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string) as Record<string, unknown>;
        if (!parsed['schemas'] || !parsed['basePdf']) {
          setError('Invalid template file: missing "schemas" or "basePdf" fields.');
          return;
        }
        setUploadPending({ template: uploadTarget, parsedJson: parsed, fileName: file.name });
      } catch {
        setError('Could not parse JSON file. Make sure it is a valid template export.');
      }
    };
    reader.readAsText(file);
  }

  async function handleUploadConfirm(comment: string, createdBy: string): Promise<void> {
    if (!uploadPending) return;
    const { template, parsedJson } = uploadPending;

    try {
      setUploading(true);
      await saveTemplate({
        id: template.id,
        label: template.label,
        labelWidthMm: template.labelWidthMm,
        labelHeightMm: template.labelHeightMm,
        description: template.description,
        comment,
        createdBy,
        template: parsedJson,
      });

      // Refresh list to show updated version count
      const updated = await fetchTemplates();
      setTemplates(updated);
      setUploadPending(null);
      setUploadTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  function handleUploadCancel(): void {
    setUploadPending(null);
    setUploadTarget(null);
  }

  if (loading) return <div className="status-msg">Loading templates…</div>;
  if (error) return <div className="status-msg status-msg--error">Error: {error}</div>;

  return (
    <div className="page">
      <div className="page__header">
        <h2>Templates</h2>
        <label style={{ marginLeft: 'auto', marginRight: '1rem', fontSize: '0.9em' }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived ({templates.filter((t) => t.archived).length})
        </label>
        <button className="btn btn--primary" onClick={() => navigate('/templates/new')}>
          + New Template
        </button>
      </div>

      {/* Hidden file input shared across all upload buttons */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {templates.length === 0 ? (
        <div className="empty-state">
          <p>No templates yet.</p>
          <button className="btn btn--primary" onClick={() => navigate('/templates/new')}>
            Create your first template
          </button>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Label</th>
              <th>ID</th>
              <th>Size (mm)</th>
              <th>Bleed</th>
              <th>Description</th>
              <th>Active Version</th>
              <th>Versions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.filter((t) => showArchived || !t.archived).map((t) => (
              <tr key={t.id} style={t.archived ? { opacity: 0.55 } : undefined}>
                <td className="td--bold">{t.label}{t.archived ? ' (archived)' : ''}</td>
                <td className="td--mono">{t.id}</td>
                <td>
                  {t.labelWidthMm} × {t.labelHeightMm}
                  {t.sizeInPoints && (
                    <div>
                      <button
                        className="btn btn--sm"
                        title="Created by an older designer version: the page is stored in points (2.83x too large in the editor). Labels already print at the right size; this rescales the template itself as a new version."
                        onClick={() => void handleResize({ widthMm: t.labelWidthMm, heightMm: t.labelHeightMm, mode: 'scale', createdBy: localStorage.getItem('label_designer_created_by') || 'designer' }, t)}
                      >
                        ⚠ Fix size
                      </button>
                    </div>
                  )}
                </td>
                <td>
                  <button className="btn btn--sm btn--ghost" onClick={() => void handleBleed(t)} title="Print bleed per side">
                    {t.bleedMm == null ? 'default' : `${t.bleedMm} mm`}
                  </button>
                </td>
                <td>{t.description || '—'}</td>
                <td className="td--mono">{t.activeVersion ?? '—'}</td>
                <td>{t.versionCount}</td>
                <td className="td--actions">
                  <button
                    className="btn btn--sm"
                    onClick={() => navigate(`/templates/${t.id}/edit`)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn--sm btn--ghost"
                    onClick={() => navigate(`/templates/${t.id}/versions`)}
                  >
                    Versions
                  </button>
                  <button
                    className="btn btn--sm btn--ghost"
                    onClick={() => void handleDownload(t)}
                    disabled={downloading === t.id}
                    title="Download template JSON"
                  >
                    {downloading === t.id ? '…' : '↓ Download'}
                  </button>
                  <button
                    className="btn btn--sm btn--ghost"
                    onClick={() => handleUploadClick(t)}
                    title="Upload new version from JSON file"
                  >
                    ↑ Upload
                  </button>
                  <button className="btn btn--sm btn--ghost" onClick={() => setResizing(t)} title="Change the label size">
                    Resize
                  </button>
                  <button className="btn btn--sm btn--ghost" onClick={() => void handleRename(t)} title="Rename (the ID stays)">
                    Rename
                  </button>
                  <button
                    className="btn btn--sm btn--ghost"
                    onClick={() => void handleArchive(t, !t.archived)}
                    title={t.archived ? 'Show it in Filling Master again' : 'Hide it in Filling Master (still renders)'}
                  >
                    {t.archived ? 'Unarchive' : 'Archive'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {uploadPending && (
        <UploadTemplateDialog
          templateLabel={uploadPending.template.label}
          fileName={uploadPending.fileName}
          onConfirm={(comment, createdBy) => void handleUploadConfirm(comment, createdBy)}
          onCancel={handleUploadCancel}
        />
      )}

      {uploading && <div className="status-msg">Uploading…</div>}

      {resizing && (
        <ResizeTemplateDialog
          templateLabel={resizing.label}
          widthMm={resizing.labelWidthMm}
          heightMm={resizing.labelHeightMm}
          onConfirm={(r) => void handleResize(r)}
          onCancel={() => setResizing(null)}
        />
      )}
    </div>
  );
}
